/**
 * mammpro.com Predictive Search — Recent Searches + Match-Highlighting + Cache TTL
 * ============================================================================
 *
 * Lightweight layer on top of Pebble's <predictive-search> web component.
 * We don't replace the component — we listen to its DOM mutations and
 * augment the rendered markup with:
 *
 *   1. Recent searches (localStorage, max N entries, with delete buttons)
 *   2. Match-highlighting on result text (bold matched substring)
 *   3. Cache TTL on the component's internal cachedMap
 *      (5 min lifetime, 50 entries max — prevents unbounded growth)
 *   4. Storing each successful query in the recent list when user submits
 *      or clicks a result
 *
 * This script runs once (the script tag has `defer` and we self-init on
 * DOMContentLoaded). It then watches the document for predictive-search
 * elements and enhances them.
 */

(function () {
  "use strict";

  // Text der Bewertung je Sprache (aus den Sprachdateien über data-mamm-rating-label)
  function ratingLabel(wert, anzahl) {
    const el = document.querySelector("[data-mamm-rating-label]");
    const tpl = (el && el.getAttribute("data-mamm-rating-label")) || "__WERT__ von 5 Sternen, __ANZAHL__ Bewertungen";
    return tpl.replace("__WERT__", wert).replace("__ANZAHL__", anzahl);
  }

  const STORAGE_KEY = "mamm-search-recent";
  const STORAGE_MAX_DEFAULT = 5;
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const CACHE_MAX_ENTRIES = 50;

  const RECENT_PRODUCTS_KEY = "mamm-recently-viewed";
  const RECENT_PRODUCTS_MAX = 8;
  const RECENT_PRODUCT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  // ----------------------------------------------------------------
  // localStorage helpers
  // ----------------------------------------------------------------

  function readRecent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Defensive: only keep string entries, deduped
      const seen = new Set();
      const out = [];
      for (const entry of parsed) {
        if (typeof entry !== "string") continue;
        const trimmed = entry.trim();
        if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
        seen.add(trimmed.toLowerCase());
        out.push(trimmed);
      }
      return out;
    } catch {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // sessionStorage may be full or unavailable — silently ignore
    }
  }

  function addRecent(term, max) {
    if (typeof term !== "string") return;
    const trimmed = term.trim();
    if (trimmed.length < 2) return; // don't store single chars
    const list = readRecent();
    // Remove existing case-insensitive match, then prepend
    const filtered = list.filter(
      (entry) => entry.toLowerCase() !== trimmed.toLowerCase()
    );
    filtered.unshift(trimmed);
    writeRecent(filtered.slice(0, max));
  }

  function removeRecent(term) {
    const list = readRecent().filter(
      (entry) => entry.toLowerCase() !== term.toLowerCase()
    );
    writeRecent(list);
  }

  function clearRecent() {
    writeRecent([]);
  }

  // ----------------------------------------------------------------
  // Recently Viewed Products — track + read from localStorage
  // ----------------------------------------------------------------

  function readRecentProducts() {
    try {
      const raw = localStorage.getItem(RECENT_PRODUCTS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const now = Date.now();
      // TTL prune: drop entries older than 30 days
      return parsed.filter(
        (p) =>
          p &&
          typeof p === "object" &&
          typeof p.handle === "string" &&
          (!p.viewed_at || now - p.viewed_at < RECENT_PRODUCT_TTL_MS)
      );
    } catch {
      return [];
    }
  }

  function writeRecentProducts(list) {
    try {
      localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(list));
    } catch {
      // storage may be full
    }
  }

  function clearRecentProducts() {
    writeRecentProducts([]);
  }

  /**
   * Detect a PDP and persist its product data into the recently-viewed
   * localStorage list. Source-of-truth is the inline JSON tag the Liquid
   * layout injects (`<script data-mamm-pdp-product>`), which already
   * carries the Judge.me rating + count from the metafield. Falls back
   * to /products/HANDLE.js if the inline tag is missing (defensive).
   */
  async function trackRecentlyViewed() {
    const path = window.location.pathname;
    const match = path.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = match[1];

    const list = readRecentProducts();
    if (list[0] && list[0].handle === handle) return;

    let entry = null;

    // Preferred source: server-injected JSON with rating + count
    const tag = document.querySelector("script[data-mamm-pdp-product]");
    if (tag) {
      try {
        const data = JSON.parse(tag.textContent);
        if (data && data.handle === handle) {
          entry = {
            handle: data.handle,
            title: data.title,
            type: data.type || "",
            url: data.url,
            image: data.image,
            price: data.price,
            compare_at_price: data.compare_at_price,
            available: !!data.available,
            rating: Number(data.rating) || 0,
            rating_count: Number(data.rating_count) || 0,
            viewed_at: Date.now(),
          };
        }
      } catch {
        // fall through to .js fetch
      }
    }

    // Fallback: /products/HANDLE.js (no rating data, but still useful)
    if (!entry) {
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return;
        const product = await res.json();
        if (!product || !product.handle) return;
        const variant = product.variants?.[0];
        entry = {
          handle: product.handle,
          title: product.title,
          type: product.type || product.product_type || "",
          url: product.url || `/products/${product.handle}`,
          image:
            product.featured_image ||
            (product.images && product.images[0]) ||
            "",
          price: variant?.price ?? product.price,
          compare_at_price:
            variant?.compare_at_price ?? product.compare_at_price ?? null,
          available: variant?.available ?? product.available ?? true,
          rating: 0,
          rating_count: 0,
          viewed_at: Date.now(),
        };
      } catch {
        return;
      }
    }

    if (!entry) return;
    const filtered = list.filter((p) => p.handle !== handle);
    filtered.unshift(entry);
    writeRecentProducts(filtered.slice(0, RECENT_PRODUCTS_MAX));
  }

  /**
   * Rewrite a Shopify CDN image URL with a target width parameter.
   * Shopify's CDN supports ?width=NN for on-the-fly resizing — used to
   * generate srcset variants for retina-crisp cards. Falls back to the
   * original URL if it doesn't look like a Shopify CDN image.
   */
  function upgradeShopifyImage(url, width) {
    if (!url || typeof url !== "string") return url;
    try {
      const u = new URL(url, window.location.origin);
      // Shopify CDN: cdn.shopify.com or *.myshopify.com
      if (
        /(cdn\.shopify\.com|\.myshopify\.com)$/i.test(u.hostname) ||
        /\/cdn\/shop\//i.test(u.pathname)
      ) {
        u.searchParams.set("width", String(width));
        return u.toString();
      }
      return url;
    } catch {
      return url;
    }
  }

  /**
   * Format a Shopify-style price into a localized currency string.
   *
   * Shopify's standard price formats:
   *   - Integer (number)        → cents          → divide by 100
   *   - String with decimal     → already major  → parse as float
   *   - String integer          → cents          → divide by 100
   *
   * Earlier the function used a "if value > 1000 it's cents" heuristic
   * which broke for cheap items (CHF 5.21 was rendered as CHF 521.00).
   * Detecting by TYPE not magnitude is robust.
   */
  function formatMoney(value) {
    if (value == null || value === "") return "";
    let major;
    if (typeof value === "number") {
      major = value / 100;
    } else if (typeof value === "string") {
      if (value.includes(".")) {
        major = parseFloat(value);
      } else {
        const intCents = parseInt(value, 10);
        if (!Number.isFinite(intCents)) return "";
        major = intCents / 100;
      }
    } else {
      return "";
    }
    if (!Number.isFinite(major)) return "";
    try {
      return new Intl.NumberFormat("de-CH", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "CHF",
        minimumFractionDigits: 2,
      }).format(major);
    } catch {
      return `CHF ${major.toFixed(2)}`;
    }
  }

  function renderRecentlyViewed(rootEl) {
    const section = rootEl.querySelector("[data-mamm-recently-viewed]");
    const list = rootEl.querySelector("[data-mamm-recently-viewed-list]");
    if (!section || !list) return;

    const products = readRecentProducts().slice(0, 4);
    if (products.length === 0) {
      section.hidden = true;
      list.innerHTML = "";
      return;
    }

    const frag = document.createDocumentFragment();
    for (const p of products) {
      const onSale =
        p.compare_at_price != null &&
        Number(p.compare_at_price) > Number(p.price);

      const li = document.createElement("li");
      li.className = "mamm-search-card";
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");

      const a = document.createElement("a");
      a.className = "mamm-search-card__link";
      a.href = p.url;
      a.setAttribute("data-mamm-search-result", "");

      // Media
      const media = document.createElement("div");
      media.className = "mamm-search-card__media";

      if (p.image && /^(https?:|\/\/|\/)/.test(p.image)) {
        const img = document.createElement("img");
        img.className = "mamm-search-card__image";
        // Match the snippet's srcset spec — up to 880px for 4x DPR
        img.src = upgradeShopifyImage(p.image, 880);
        img.srcset = [220, 330, 440, 550, 660, 880]
          .map((w) => `${upgradeShopifyImage(p.image, w)} ${w}w`)
          .join(", ");
        img.sizes = "(min-width: 768px) 220px, 170px";
        img.alt = p.title || "";
        img.loading = "lazy";
        img.width = 880;
        img.height = 880;
        media.appendChild(img);
      } else {
        const ph = document.createElement("div");
        ph.className =
          "mamm-search-card__image mamm-search-card__image--placeholder";
        media.appendChild(ph);
      }

      if (onSale) {
        const badge = document.createElement("span");
        badge.className =
          "mamm-search-card__badge mamm-search-card__badge--sale";
        badge.textContent = "Sale";
        media.appendChild(badge);
      } else if (p.available === false) {
        const badge = document.createElement("span");
        badge.className =
          "mamm-search-card__badge mamm-search-card__badge--soldout";
        badge.textContent = "Ausverkauft";
        media.appendChild(badge);
      }

      // Body
      const body = document.createElement("div");
      body.className = "mamm-search-card__body";

      if (p.type) {
        const type = document.createElement("span");
        type.className = "mamm-search-card__type";
        type.textContent = p.type;
        body.appendChild(type);
      }

      const title = document.createElement("span");
      title.className = "mamm-search-card__title";
      title.textContent = p.title || "";
      body.appendChild(title);

      // Rating row (always rendered — empty placeholder when no reviews,
      // so cards in the grid keep matching heights).
      if (p.rating && p.rating_count && Number(p.rating_count) > 0) {
        const rating = document.createElement("span");
        rating.className = "mamm-search-card__rating";
        rating.setAttribute(
          "aria-label",
          ratingLabel(Number(p.rating).toFixed(1), p.rating_count)
        );
        rating.innerHTML =
          '<svg class="mamm-search-card__star" viewBox="0 0 14 13" width="12" height="12" aria-hidden="true">' +
          '<path d="M7 0L8.77746 4.55353L13.6574 4.83688L9.87599 7.93447L11.1145 12.6631L7 10.024L2.8855 12.6631L4.12401 7.93447L0.342604 4.83688L5.22254 4.55353L7 0Z" fill="currentColor"/>' +
          "</svg>";
        const value = document.createElement("span");
        value.className = "mamm-search-card__rating-value";
        value.textContent = Number(p.rating).toFixed(1);
        const count = document.createElement("span");
        count.className = "mamm-search-card__rating-count";
        count.textContent = `(${p.rating_count})`;
        rating.appendChild(value);
        rating.appendChild(count);
        body.appendChild(rating);
      } else {
        const empty = document.createElement("span");
        empty.className =
          "mamm-search-card__rating mamm-search-card__rating--empty";
        empty.setAttribute("aria-hidden", "true");
        body.appendChild(empty);
      }

      const price = document.createElement("span");
      price.className = "mamm-search-card__price";
      const actual = document.createElement("span");
      actual.className =
        "mamm-search-card__price-actual" +
        (onSale ? " mamm-search-card__price-actual--sale" : "");
      actual.textContent = formatMoney(p.price);
      price.appendChild(actual);
      if (onSale) {
        const compare = document.createElement("span");
        compare.className = "mamm-search-card__price-compare";
        compare.textContent = formatMoney(p.compare_at_price);
        price.appendChild(compare);
      }
      body.appendChild(price);

      a.appendChild(media);
      a.appendChild(body);
      li.appendChild(a);
      frag.appendChild(li);
    }

    list.innerHTML = "";
    list.appendChild(frag);
    section.hidden = false;
  }

  // ----------------------------------------------------------------
  // DOM rendering: recent searches into the empty state
  // ----------------------------------------------------------------

  function renderRecent(rootEl) {
    const section = rootEl.querySelector("[data-mamm-recent]");
    const list = rootEl.querySelector("[data-mamm-recent-list]");
    if (!section || !list) return;

    const recents = readRecent();
    if (recents.length === 0) {
      section.hidden = true;
      list.innerHTML = "";
      return;
    }

    const searchUrl =
      (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.search_url) ||
      "/search";

    const frag = document.createDocumentFragment();
    for (const term of recents) {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.className = "mamm-search__pill mamm-search__pill--recent";
      a.href = `${searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(
        term
      )}`;

      const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      icon.setAttribute("class", "mamm-search__pill-icon");
      icon.setAttribute("viewBox", "0 0 16 16");
      icon.setAttribute("width", "14");
      icon.setAttribute("height", "14");
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML =
        '<circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.5v3.7L10 9.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>';

      const text = document.createElement("span");
      text.className = "mamm-search__pill-text";
      text.textContent = term;

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "mamm-search__pill-remove";
      removeBtn.setAttribute("aria-label", `"${term}" entfernen`);
      removeBtn.innerHTML =
        '<svg viewBox="0 0 14 14" width="10" height="10" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" d="M3 3l8 8M3 11L11 3"/></svg>';
      removeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeRecent(term);
        renderRecent(rootEl);
      });

      a.appendChild(icon);
      a.appendChild(text);
      a.appendChild(removeBtn);
      li.appendChild(a);
      frag.appendChild(li);
    }

    list.innerHTML = "";
    list.appendChild(frag);
    section.hidden = false;
  }

  // ----------------------------------------------------------------
  // Match-highlighting: wrap matched substring in <mark>
  // ----------------------------------------------------------------

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function highlightMatches(rootEl, query) {
    if (!query || query.length < 2) return;
    const pattern = new RegExp(`(${escapeRegex(query)})`, "gi");
    const lowerQuery = query.toLowerCase();

    const targets = rootEl.querySelectorAll("[data-mamm-highlightable]");
    targets.forEach((el) => {
      const text = el.textContent;
      if (!text) return;
      // Don't mutate if already contains marks
      if (el.querySelector("mark")) return;
      // Split keeps capturing-group matches as array entries — safe DOM
      // manipulation only (no innerHTML with user-controlled strings).
      const parts = text.split(pattern);
      if (parts.length === 1) return;
      el.textContent = "";
      parts.forEach((part) => {
        if (!part) return;
        if (part.toLowerCase() === lowerQuery) {
          const mark = document.createElement("mark");
          mark.textContent = part;
          el.appendChild(mark);
        } else {
          el.appendChild(document.createTextNode(part));
        }
      });
    });
  }

  // ----------------------------------------------------------------
  // Cache TTL: prune Pebble's internal Map after each render
  // ----------------------------------------------------------------

  const cacheMeta = new WeakMap(); // root → Map<url, timestamp>

  function pruneCache(root) {
    if (!root.cachedMap || !(root.cachedMap instanceof Map)) return;
    const meta = cacheMeta.get(root) || new Map();
    const now = Date.now();

    // Time-based prune
    for (const [key, ts] of meta) {
      if (now - ts > CACHE_TTL_MS) {
        root.cachedMap.delete(key);
        meta.delete(key);
      }
    }
    // Size-based prune (LRU-ish: drop oldest by timestamp)
    if (root.cachedMap.size > CACHE_MAX_ENTRIES) {
      const sorted = [...meta.entries()].sort((a, b) => a[1] - b[1]);
      const toDrop = sorted.slice(0, root.cachedMap.size - CACHE_MAX_ENTRIES);
      for (const [key] of toDrop) {
        root.cachedMap.delete(key);
        meta.delete(key);
      }
    }
    cacheMeta.set(root, meta);
  }

  function trackCacheEntry(root) {
    if (!root.cachedMap) return;
    const meta = cacheMeta.get(root) || new Map();
    // Mark the most recently added entry with current timestamp
    const lastKey = [...root.cachedMap.keys()].pop();
    if (lastKey) meta.set(lastKey, Date.now());
    cacheMeta.set(root, meta);
    pruneCache(root);
  }

  // ----------------------------------------------------------------
  // Recent-search persistence on submit / result-click
  // ----------------------------------------------------------------

  function getCurrentQuery(root) {
    const input = root.querySelector(".mamm-search__input");
    return input ? input.value.trim() : "";
  }

  function persistCurrentQuery(root, max) {
    const q = getCurrentQuery(root);
    if (q) addRecent(q, max);
  }

  // ----------------------------------------------------------------
  // Setup per <predictive-search>
  // ----------------------------------------------------------------

  /**
   * Portal pattern for the desktop header modal.
   *
   * On open: clone a 0-size placeholder into the original DOM slot so the
   * header's flex layout doesn't reflow, then physically move the
   * predictive-search element to a portal container appended to <body>.
   * This isolates it from any ancestor that creates a stacking context
   * or applies transforms (Pebble's sticky/scrolled header), letting our
   * `position: fixed` modal CSS center reliably against the viewport.
   *
   * On close: reverse — move the element back into its placeholder slot
   * and remove the placeholder.
   *
   * On mobile (<768px) the modal is full-screen; portal still helps but
   * the original drawer-style positioning would also work. We portal
   * uniformly for consistent state machine.
   */
  function setupHeaderPortal(root) {
    let portal = document.getElementById("mamm-search-portal");
    if (!portal) {
      portal = document.createElement("div");
      portal.id = "mamm-search-portal";
      portal.className = "mamm-search-portal";
      document.body.appendChild(portal);
    }

    const placeholder = document.createComment("mamm-search-portal-anchor");
    let isPortalled = false;

    /* ============================================================
       iOS WebKit touch-lock — JS layer
       ------------------------------------------------------------
       CSS alone (touch-action, overscroll-behavior, position:fixed)
       does not reliably defeat iOS Chrome's rubber-band on a fixed
       modal. Even with body locked, iOS still allows a tiny
       horizontal pull on the modal element itself.

       The only bulletproof fix: a passive:false touchmove listener
       at document level that calls preventDefault() on any gesture
       that ISN'T inside a known scrollable area (the carousel for
       horizontal, .mamm-search__inner for vertical). This is the
       canonical iOS body-scroll-lock pattern.
       ============================================================ */
    let touchStartX = 0;
    let touchStartY = 0;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (t) {
        touchStartX = t.clientX;
        touchStartY = t.clientY;
      }
    };

    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;

      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Find the scrollable ancestor of the touch target
      const target = e.target;
      const carousel = target.closest && target.closest(".mamm-search__carousel");
      const innerScroll = target.closest && target.closest(".mamm-search__inner");

      // Horizontal gesture
      if (absDx > absDy) {
        if (carousel) {
          // Inside a carousel: allow horizontal scroll, but check
          // boundaries so we don't rubber-band off the ends.
          const atStart = carousel.scrollLeft <= 0 && dx > 0;
          const atEnd =
            carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 1 && dx < 0;
          if (atStart || atEnd) {
            e.preventDefault();
          }
          // Otherwise: native scroll runs, modal stays put
          return;
        }
        // Horizontal swipe outside any carousel → block completely
        e.preventDefault();
        return;
      }

      // Vertical gesture
      if (innerScroll) {
        // Allow vertical scroll inside the inner panel, but block
        // overscroll at the edges.
        const atTop = innerScroll.scrollTop <= 0 && dy > 0;
        const atBottom =
          innerScroll.scrollTop + innerScroll.clientHeight >= innerScroll.scrollHeight - 1 &&
          dy < 0;
        if (atTop || atBottom) {
          e.preventDefault();
        }
        return;
      }
      // Vertical swipe outside the inner scroll area → block
      e.preventDefault();
    };

    const lockBodyTouch = () => {
      document.addEventListener("touchstart", onTouchStart, { passive: true });
      document.addEventListener("touchmove", onTouchMove, { passive: false });
    };

    const unlockBodyTouch = () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };

    const portalIn = () => {
      if (isPortalled) return;
      // Capture focus state BEFORE the move — appendChild detaches the
      // node and the browser drops focus. We restore it on the next
      // frame so the user can type immediately after one click.
      const activeBefore = document.activeElement;
      const focusInside =
        activeBefore && activeBefore !== document.body && root.contains(activeBefore);
      const selectionStart =
        focusInside && "selectionStart" in activeBefore ? activeBefore.selectionStart : null;
      const selectionEnd =
        focusInside && "selectionEnd" in activeBefore ? activeBefore.selectionEnd : null;

      // Drop the anchor where the element currently lives so we can
      // re-insert it in the same spot when closing
      root.parentNode?.insertBefore(placeholder, root);
      portal.appendChild(root);
      isPortalled = true;

      // Engage iOS touch-lock — kills the residual horizontal pull
      lockBodyTouch();

      if (focusInside) {
        // Use rAF to wait for layout after the move; preventScroll keeps
        // the page from jumping when focus shifts to the centered modal.
        requestAnimationFrame(() => {
          try {
            activeBefore.focus({ preventScroll: true });
            if (selectionStart !== null && "setSelectionRange" in activeBefore) {
              activeBefore.setSelectionRange(selectionStart, selectionEnd);
            }
          } catch {
            // Element no longer focusable — silent fallback
          }
        });
      }
    };

    const portalOut = () => {
      if (!isPortalled) return;
      const anchor = placeholder.parentNode ? placeholder : null;
      if (anchor) {
        anchor.parentNode.insertBefore(root, anchor);
        anchor.parentNode.removeChild(anchor);
      } else {
        // Anchor lost (theme editor reload?). Re-attach to body as a fallback.
        document.body.appendChild(root);
      }
      isPortalled = false;

      // Release iOS touch-lock so the rest of the page is interactive again
      unlockBodyTouch();
    };

    // React to Pebble's open/close state via class mutations on the host.
    const classObserver = new MutationObserver(() => {
      const isOpen = root.classList.contains("predictive-search-open");
      if (isOpen) {
        portalIn();
      } else {
        portalOut();
      }
    });
    classObserver.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // If we re-init while already open (theme editor hot-reload), portal now.
    if (root.classList.contains("predictive-search-open")) {
      portalIn();
    }
  }

  function setupRoot(root) {
    if (root.dataset.mammSearchInit === "true") return;
    root.dataset.mammSearchInit = "true";

    const max = Number(root.dataset.recentMax) || STORAGE_MAX_DEFAULT;

    // Portal the predictive-search element to body level when opened
    // (header context only, ≥ 768px viewport). This guarantees:
    //   - position:fixed centers against the viewport (not against any
    //     transformed ancestor in the header's animation chain)
    //   - Pebble's `.header__search .search__content` rules no longer
    //     match (their selector requires .header__search ancestry), so
    //     our modal CSS owns the rendering uncontested
    //   - The header layout doesn't reflow — a placeholder takes the
    //     element's original slot for the duration of the modal
    if (root.dataset.context === "header") {
      setupHeaderPortal(root);
    }

    // Initial render of recent searches + recently-viewed products
    renderRecent(root);
    renderRecentlyViewed(root);

    // Clear-all button: searches
    const clearBtn = root.querySelector("[data-mamm-clear-recent]");
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        clearRecent();
        renderRecent(root);
      });
    }

    // Clear-all button: recently viewed products
    const clearViewedBtn = root.querySelector(
      "[data-mamm-clear-recently-viewed]"
    );
    if (clearViewedBtn) {
      clearViewedBtn.addEventListener("click", (e) => {
        e.preventDefault();
        clearRecentProducts();
        renderRecentlyViewed(root);
      });
    }

    // Intercept form submission entirely — all searches happen inside
    // the modal, never navigate to /search page. Pressing Enter just
    // persists the query in localStorage and keeps the predictive
    // results visible. The "view all" button is hidden via CSS so this
    // covers it as well.
    const form = root.querySelector("form");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        persistCurrentQuery(root, max);
        // Keep focus on the input so user can refine without re-clicking
        const input = root.querySelector(".mamm-search__input");
        if (input) {
          requestAnimationFrame(() =>
            input.focus({ preventScroll: true })
          );
        }
      });
    }

    // Persist on result-link click (product cards, query pills, meta links)
    root.addEventListener("click", (e) => {
      const link = e.target.closest(
        "[data-mamm-search-result], .mamm-search__results [href], .mamm-search__no-results [href]"
      );
      if (!link) return;
      // Skip the recent-pill remove buttons
      if (e.target.closest(".mamm-search__pill-remove")) return;
      persistCurrentQuery(root, max);
    });

    // Pills (Recent + Trending + Vorschläge) — intercept clicks so the
    // search runs INSIDE the modal instead of navigating to /search.
    // Sets the input value, dispatches the input event so Pebble's
    // predictive search fires its onInputDebounced + fetch.
    root.addEventListener(
      "click",
      (e) => {
        const pill = e.target.closest(".mamm-search__pill");
        if (!pill || !root.contains(pill)) return;
        // Don't intercept the X / remove button on recent pills
        if (e.target.closest(".mamm-search__pill-remove")) return;

        e.preventDefault();
        e.stopPropagation();

        const text =
          pill.querySelector(".mamm-search__pill-text")?.textContent?.trim() ||
          pill.textContent.trim();
        if (!text) return;

        const inputEl = root.querySelector(".mamm-search__input");
        if (!inputEl) return;
        inputEl.value = text;
        // Fire input event so Pebble's onInputDebounced kicks in
        inputEl.dispatchEvent(new Event("input", { bubbles: true }));
        requestAnimationFrame(() =>
          inputEl.focus({ preventScroll: true })
        );
      },
      true // capture phase, runs before any anchor's default navigation
    );

    // Override Pebble's `clear()` so it ALWAYS just empties the input
    // and refreshes the empty state — it NEVER closes the modal. Three
    // call sites:
    //   1. onChange() when query becomes empty (typing → empty)
    //   2. X button (`on:click="/clear"`)
    //   3. Esc key (Pebble's escKeyPressHandler also calls this.clear())
    //
    // The user's UX rule: modal stays open when clearing via input or X,
    // closes ONLY via "Abbrechen" or backdrop click. Esc is a power-user
    // shortcut for backdrop-equivalent — we restore that via a separate
    // keydown listener below (Pebble's escKey would otherwise be a no-op
    // now that clear() doesn't close).
    if (typeof root.clear === "function") {
      root.clear = function (event) {
        event?.preventDefault?.();
        if (root.refs?.searchInput) {
          root.refs.searchInput.value = "";
          root.refs.searchInput.focus({ preventScroll: true });
        }
        root.removeAttribute("results");
        renderRecent(root);
        renderRecentlyViewed(root);
      };
    }

    // Esc still closes the modal (power-user shortcut, equivalent to
    // clicking the backdrop). Pebble's escKey would call our overridden
    // clear() which no longer closes — so we add our own keydown
    // listener that bypasses clear and goes straight to toggleSearchState.
    if (!root.__mammEscBound) {
      root.__mammEscBound = true;
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        if (!root.classList.contains("predictive-search-open")) return;
        root.toggleSearchState?.(false);
      });
    }

    // Expand Shopify's predictive search field-coverage. By default the
    // /search/suggest endpoint searches only:
    //   title, product_type, variants.title, vendor
    // → SKUs, barcodes, tags, and product descriptions are NOT searched.
    // We override setupURL() on this instance to pass an explicit fields
    // list that includes everything useful for a catalog like mamm:
    //   - variants.sku       → article numbers (e.g. "TU-0003")
    //   - variants.barcode   → EAN / UPC codes
    //   - tag                → curated tags ("bestseller", "neu", "sale")
    //   - body               → product descriptions (catches mentions
    //                          of car brands, applications, ingredients)
    if (typeof root.setupURL === "function") {
      root.setupURL = function () {
        const shopUrl = window.FoxTheme?.routes?.shop_url || "";
        const predictiveUrl =
          window.FoxTheme?.routes?.predictive_search_url || "/search/suggest";
        const url = new URL(`${shopUrl}${predictiveUrl}`, window.location.origin);
        const term = root.refs?.searchInput?.value?.trim() || "";
        url.searchParams.set("q", term);
        url.searchParams.set("resources[limit]", root.dataset.resultsLimit || 3);
        url.searchParams.set("resources[limit_scope]", "each");
        url.searchParams.set(
          "resources[options][fields]",
          "title,product_type,vendor,variants.title,variants.sku,variants.barcode,tag,body"
        );
        url.searchParams.set("section_id", root.dataset.sectionId || "");
        return url;
      };
    }

    // Watch the results container for re-renders (Pebble component swaps
    // innerHTML on each fetch). After each swap: highlight + track cache.
    const resultsContainer = root.querySelector(
      '[id^="PredictiveSearchResults-"]'
    );
    if (resultsContainer) {
      const observer = new MutationObserver(() => {
        const q = getCurrentQuery(root);
        highlightMatches(resultsContainer, q);
        trackCacheEntry(root);
        // On every results-render, also refresh the recent list and
        // recently-viewed list — fresh data when user re-engages.
        renderRecent(root);
        renderRecentlyViewed(root);
      });
      observer.observe(resultsContainer, {
        childList: true,
        subtree: true,
      });
    }

    // Also re-render recents whenever input is cleared (user erases text)
    const input = root.querySelector(".mamm-search__input");
    if (input) {
      input.addEventListener("input", () => {
        if (input.value.trim().length === 0) {
          renderRecent(root);
          renderRecentlyViewed(root);
        }
      });
    }
  }

  // ----------------------------------------------------------------
  // Boot
  // ----------------------------------------------------------------

  function init() {
    // Track this page-view if it's a PDP (regardless of whether the
    // predictive-search element exists on this page — PDPs typically
    // don't keep the search drawer mounted, but we still want to log).
    trackRecentlyViewed();
    document.querySelectorAll("predictive-search.mamm-search").forEach(setupRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init when *new* predictive-search elements are inserted into the DOM
  // (theme editor section reload, drawer hot-mount). Targeted observer
  // checks only added nodes, not every mutation everywhere.
  const bodyObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const matches = node.matches && node.matches("predictive-search.mamm-search");
        if (matches) {
          setupRoot(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll("predictive-search.mamm-search").forEach(setupRoot);
        }
      }
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
})();
